import React, { useState } from 'react';
import axios from 'axios';
import { API } from '../../constants/api';

const AdminRemindersTab = ({ reminders, loading, filters, setFilters, pagination, setPagination, reloadReminders }) => {
  
  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { label: 'Activo', color: 'bg-green-100 text-green-800' },
      converted: { label: 'Convertido', color: 'bg-blue-100 text-blue-800' },
      cancelled: { label: 'Cancelado', color: 'bg-gray-100 text-gray-800' },
      bounced: { label: 'Rebotado', color: 'bg-red-100 text-red-800' }
    };
    
    const config = statusConfig[status] || statusConfig.active;
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, skip: 0 })); // Reset pagination
    setTimeout(reloadReminders, 100); // Reload with new filters
  };

  const handlePageChange = (newSkip) => {
    setPagination(prev => ({ ...prev, skip: newSkip }));
    setTimeout(reloadReminders, 100);
  };

  const exportToCSV = () => {
    if (reminders.length === 0) return;
    
    const headers = ['Email', 'Perfil', 'Fecha Suscripción', 'Estado', 'Primer Recordatorio', 'Segundo Recordatorio'];
    const csvContent = [
      headers.join(','),
      ...reminders.map(reminder => [
        reminder.email,
        reminder.profile_name || 'N/A',
        formatDate(reminder.subscribed_at),
        reminder.status,
        reminder.first_reminder_sent ? 'Enviado' : 'Pendiente',
        reminder.second_reminder_sent ? 'Enviado' : 'Pendiente'
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `recordatorios_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="space-y-6">
      {/* Header y filtros */}
      <div className="bg-white card-shadow-lg rounded-xl p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h3 className="text-lg font-semibold text-black">Recordatorios por Email</h3>
            <p className="text-gray-600 text-sm">Gestión de suscripciones a recordatorios</p>
          </div>
          <button
            onClick={exportToCSV}
            disabled={reminders.length === 0}
            className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            📁 Exportar CSV
          </button>
        </div>

        {/* Filtros */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Estado</label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="form-input"
            >
              <option value="">Todos los estados</option>
              <option value="active">Activo</option>
              <option value="converted">Convertido</option>
              <option value="cancelled">Cancelado</option>
              <option value="bounced">Rebotado</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Perfil</label>
            <input
              type="text"
              value={filters.profile}
              onChange={(e) => handleFilterChange('profile', e.target.value)}
              placeholder="ID del perfil"
              className="form-input"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={() => {
                setFilters({ status: '', profile: '' });
                setPagination(prev => ({ ...prev, skip: 0 }));
                setTimeout(reloadReminders, 100);
              }}
              className="btn-secondary w-full"
            >
              Limpiar filtros
            </button>
          </div>
        </div>
      </div>

      {/* Tabla de recordatorios */}
      <div className="bg-white card-shadow-lg rounded-xl p-6">
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-300 mx-auto"></div>
            <p className="mt-2 text-gray-600">Cargando recordatorios...</p>
          </div>
        ) : reminders.length === 0 ? (
          <div className="text-center py-8">
            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              🔔
            </div>
            <h4 className="text-lg font-semibold text-gray-900 mb-2">No hay recordatorios</h4>
            <p className="text-gray-600 text-sm">Los recordatorios aparecerán aquí cuando los visitantes se suscriban.</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Perfil
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Fecha Suscripción
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Estado
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Recordatorios
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Enlace
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {reminders.map((reminder) => (
                    <tr key={reminder.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {reminder.email}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {reminder.profile_name || 'N/A'}
                        </div>
                        <div className="text-xs text-gray-500">
                          ID: {reminder.profile_id}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(reminder.subscribed_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(reminder.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="space-y-1">
                          <div className={`flex items-center ${reminder.first_reminder_sent ? 'text-green-600' : 'text-gray-400'}`}>
                            <span className="mr-1">{reminder.first_reminder_sent ? '✅' : '⏳'}</span>
                            1er recordatorio
                          </div>
                          <div className={`flex items-center ${reminder.second_reminder_sent ? 'text-green-600' : 'text-gray-400'}`}>
                            <span className="mr-1">{reminder.second_reminder_sent ? '✅' : '⏳'}</span>
                            2do recordatorio
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {reminder.profile_slug && (
                          <a
                            href={`/p/${reminder.profile_slug}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sky-600 hover:text-sky-800 hover:underline"
                          >
                            Ver perfil →
                          </a>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Paginación */}
            {pagination.total > pagination.limit && (
              <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6 mt-6">
                <div className="flex flex-1 justify-between sm:hidden">
                  <button
                    onClick={() => handlePageChange(Math.max(0, pagination.skip - pagination.limit))}
                    disabled={pagination.skip === 0}
                    className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Anterior
                  </button>
                  <button
                    onClick={() => handlePageChange(pagination.skip + pagination.limit)}
                    disabled={pagination.skip + pagination.limit >= pagination.total}
                    className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Siguiente
                  </button>
                </div>
                <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Mostrando <span className="font-medium">{pagination.skip + 1}</span> a{' '}
                      <span className="font-medium">
                        {Math.min(pagination.skip + pagination.limit, pagination.total)}
                      </span>{' '}
                      de <span className="font-medium">{pagination.total}</span> resultados
                    </p>
                  </div>
                  <div>
                    <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                      <button
                        onClick={() => handlePageChange(Math.max(0, pagination.skip - pagination.limit))}
                        disabled={pagination.skip === 0}
                        className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50"
                      >
                        ←
                      </button>
                      <button
                        onClick={() => handlePageChange(pagination.skip + pagination.limit)}
                        disabled={pagination.skip + pagination.limit >= pagination.total}
                        className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50"
                      >
                        →
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
export default AdminRemindersTab;
